import React, { useEffect, useState } from "react";
import { NavLink, useNavigate, useParams } from "react-router-dom";
import { Image as ImageIcon, Film, AudioLines, Shuffle, Settings, LogOut, ChevronDown, FolderOpen, Check, Clapperboard, Library } from "lucide-react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Logo } from "@/components/Logo";
import { useAuth } from "@/context/AuthContext";
import { useRate } from "@/context/RateContext";
import { api } from "@/lib/api";
import NotificationBell from "@/components/NotificationBell";
import NavModelMenu from "@/components/NavModelMenu";

const TABS = [
  { id: "image", label: "Image", icon: ImageIcon },
  { id: "video", label: "Video", icon: Film },
  { id: "audio", label: "Audio", icon: AudioLines },
  { id: "remix", label: "Remix", icon: Shuffle },
];

export default function TopNav() {
  const { projectId } = useParams();
  const nav = useNavigate();
  const { user, logout } = useAuth();
  const { rate } = useRate();
  const [projects, setProjects] = useState([]);
  useEffect(() => { api.get("/projects").then((r) => setProjects(r.data)).catch(() => {}); }, []);
  const current = projects.find((p) => p.id === projectId);

  return (
    <header className="sticky top-0 z-50 bg-[#0B1013]/60 backdrop-blur-xl border-b border-white/5" data-testid="top-nav">
      <div className="h-14 px-4 flex items-center gap-4">
        <button onClick={() => nav("/projects")} data-testid="nav-logo"><Logo size={30} /></button>

        {projectId && (
          <nav className="flex items-center gap-1 ml-2" data-testid="nav-tabs">
            {TABS.map((t) => (
              <NavModelMenu key={t.id} section={t.id} projectId={projectId}>
                <NavLink to={`/studio/${projectId}/${t.id}`} data-testid={`nav-tab-${t.id}`}
                  className={({ isActive }) => `flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm transition-colors ${isActive ? "bg-zinc-800 text-white font-medium" : "text-zinc-400 hover:text-white hover:bg-zinc-800/50"}`}>
                  <t.icon className="w-4 h-4" />{t.label}
                </NavLink>
              </NavModelMenu>
            ))}
            <span className="mx-1.5 h-5 w-px bg-zinc-800" />
            <NavModelMenu section="blackfx" projectId={projectId}>
              <NavLink to={`/studio/${projectId}/blackfx`} data-testid="nav-tab-blackfx"
                className={({ isActive }) => `bfx-tab flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-semibold tracking-wide transition-colors ${isActive ? "active" : ""}`}>
                <Clapperboard className="w-4 h-4" />Black FX
              </NavLink>
            </NavModelMenu>
          </nav>
        )}

        <div className="flex-1" />

        <span className="hidden lg:inline text-[11px] font-mono text-zinc-500" data-testid="nav-rate" title="Live USD → INR rate">1 USD = ₹{rate.toFixed(2)}</span>

        {projectId && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="ax-chip" data-testid="nav-project-switcher">
                <FolderOpen className="w-3.5 h-3.5 text-zinc-400" />
                <span className="max-w-[160px] truncate">{current?.name || "Project"}</span>
                <ChevronDown className="w-3.5 h-3.5 text-zinc-500" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="bg-[#121214] border-zinc-800 text-zinc-200 w-60">
              {projects.map((p) => (
                <DropdownMenuItem key={p.id} onClick={() => nav(`/studio/${p.id}/image`)} className="focus:bg-zinc-800 cursor-pointer" data-testid={`nav-project-${p.id}`}>
                  <span className="flex-1 truncate">{p.name}</span>{p.id === projectId && <Check className="w-3.5 h-3.5 text-[#7AF25F]" />}
                </DropdownMenuItem>
              ))}
              <DropdownMenuSeparator className="bg-zinc-800" />
              <DropdownMenuItem onClick={() => nav("/projects")} className="focus:bg-zinc-800 cursor-pointer" data-testid="nav-all-projects">All projects…</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}

        <NavLink to="/library" data-testid="nav-library-link" title="Media Library"
          className={({ isActive }) => `ax-chip ${isActive ? "!border-[#7AF25F]/60 text-white" : ""}`}>
          <Library className="w-3.5 h-3.5" />Media Library
        </NavLink>

        <NotificationBell />

        <button onClick={() => nav("/settings")} data-testid="nav-settings-link" title="Settings"
          className="h-9 w-9 rounded-lg border border-zinc-800 flex items-center justify-center text-zinc-400 hover:text-white hover:border-zinc-700">
          <Settings className="w-4 h-4" />
        </button>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button data-testid="nav-avatar" className="h-9 w-9 rounded-full ax-gradient-bg text-black font-bold text-sm flex items-center justify-center">
              {(user?.display || user?.username || "A")[0].toUpperCase()}
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="bg-[#121214] border-zinc-800 text-zinc-200 w-52">
            <div className="px-2 py-1.5 text-xs text-zinc-500">{user?.email}</div>
            <DropdownMenuSeparator className="bg-zinc-800" />
            <DropdownMenuItem onClick={() => nav("/settings")} className="focus:bg-zinc-800 cursor-pointer" data-testid="nav-menu-settings"><Settings className="w-4 h-4 mr-2" />Settings</DropdownMenuItem>
            <DropdownMenuItem onClick={logout} className="focus:bg-zinc-800 cursor-pointer text-red-400" data-testid="nav-menu-logout"><LogOut className="w-4 h-4 mr-2" />Log out</DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
}
